import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api.js';

/**
 * Custom hook for managing API calls with loading states and error handling
 */
export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiConnected, setApiConnected] = useState(false);

  // Test API connection on mount
  useEffect(() => {
    const testConnection = async () => {
      try {
        const connected = await apiService.testConnection();
        setApiConnected(connected);
        if (!connected) {
          console.warn('⚠️ Backend API is not connected. Some features may not work.');
        }
      } catch (err) {
        console.error('❌ Failed to test API connection:', err);
        setApiConnected(false);
      }
    };

    testConnection();
  }, []);

  /**
   * Execute API call with loading and error handling
   */
  const executeApiCall = useCallback(async (apiCall, options = {}) => {
    const { showLoading = true, clearError = true } = options;

    if (clearError) {
      setError(null);
    }

    if (showLoading) {
      setLoading(true);
    }

    try {
      const result = await apiCall();
      return result;
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, []);

  return {
    apiService,
    loading,
    error,
    apiConnected,
    executeApiCall,
    clearError: () => setError(null),
  };
};

/**
 * Hook for managing data fetching with caching
 */
export const useApiData = (fetchFunction, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchFunction();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchFunction]);

  useEffect(() => {
    fetchData();
  }, [fetchData, ...dependencies]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch,
  };
};

/**
 * Hook for managing form submissions
 */
export const useApiSubmit = () => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const submit = useCallback(async (apiCall, options = {}) => {
    const { onSuccess, onError, resetSuccess = true } = options;

    setSubmitting(true);
    setError(null);
    if (resetSuccess) {
      setSuccess(false);
    }

    try {
      const result = await apiCall();
      setSuccess(true);
      if (onSuccess) {
        onSuccess(result);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Submission failed';
      setError(errorMessage);
      if (onError) {
        onError(err);
      }
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setSubmitting(false);
    setError(null);
    setSuccess(false);
  }, []);

  return {
    submitting,
    error,
    success,
    submit,
    reset,
  };
};

/**
 * Hook for managing file uploads
 */
export const useApiUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const upload = useCallback(async (apiCall, options = {}) => {
    const { onSuccess, onError } = options;

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      const result = await apiCall();
      setProgress(100);
      if (onSuccess) {
        onSuccess(result);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Upload failed';
      setError(errorMessage);
      if (onError) {
        onError(err);
      }
      throw err;
    } finally {
      setUploading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setUploading(false);
    setProgress(0);
    setError(null);
  }, []);

  return {
    uploading,
    progress,
    error,
    upload,
    reset,
  };
};

/**
 * Hook for managing real-time data updates
 */
export const useApiRealtime = (fetchFunction, interval = 30000) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const result = await fetchFunction();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchFunction]);

  useEffect(() => {
    fetchData();

    const intervalId = setInterval(fetchData, interval);

    return () => clearInterval(intervalId);
  }, [fetchData, interval]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
}; 